import { ReactNode, useMemo } from 'react';
import { Warning600 } from '@src/utils/colors';
import { DataQaChart, SeriesConfig } from '../components/DataQaChart';
import { GroundTruthSqlPanel } from '../components/GroundTruthSqlPanel';
import { KpiBar, KpiTileData } from '../components/KpiBar';
import { DataQaDataset } from '../data-qa-types';
import { PortfolioAnalysis } from '../media-inputs-utils';

interface PortfolioPanelProps {
  dataset: DataQaDataset;
  // Computed once by the parent (analyzePortfolio), which also derives the Portfolio
  // pill's indicator from it.
  analysis: PortfolioAnalysis;
  bookmarkWeekIso?: string;
  isColdStart: boolean;
  // Company slug (cid) -- needed to name the table in the compare-to-canonical SQL.
  company: string;
}

// Streamlit series colors: spend green, impressions blue; BPM canonical dashed.
const BPM_COLOR = Warning600;
const SPEND_SERIES: SeriesConfig[] = [
  { dataKey: 'spend', label: 'MMM Spend', color: '#2ca02c', format: 'currency', axis: 'left' },
  {
    dataKey: 'bpmSpend',
    label: 'BPM Spend',
    color: BPM_COLOR,
    format: 'currency',
    axis: 'left',
    dashed: true
  }
];
const IMPRESSIONS_SERIES: SeriesConfig[] = [
  {
    dataKey: 'impressions',
    label: 'MMM Impressions',
    color: '#1f77b4',
    format: 'number',
    axis: 'left'
  },
  {
    dataKey: 'bpmImpressions',
    label: 'BPM Impressions',
    color: BPM_COLOR,
    format: 'number',
    axis: 'left',
    dashed: true
  }
];

const money0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const money2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const int0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// Comma-joined list, capped at N with a "+K more" suffix (mirrors the Streamlit note).
const formatPlatformList = (items: string[], cap = 5) =>
  items.length <= cap
    ? items.join(', ')
    : `${items.slice(0, cap).join(', ')}, +${items.length - cap} more`;

// The all-channel totals view on the Media Inputs tab: KPIs, total spend and impressions
// charts against BPM, and the coverage/unresolved-media notes.
export const PortfolioPanel = ({
  dataset,
  analysis,
  bookmarkWeekIso,
  isColdStart,
  company
}: PortfolioPanelProps) => {
  const { portfolioStats } = dataset;
  const { chartWeeks, hasBpm, spendRanges, impressionsRanges, hasUnresolvedMedia } = analysis;
  const groundTruthWeeks = useMemo(
    () =>
      chartWeeks.map(w => ({
        weekIso: w.weekIso,
        mmmSpend: w.spend,
        mmmImpressions: w.impressions,
        bpmSpend: w.bpmSpend,
        bpmImpressions: w.bpmImpressions
      })),
    [chartWeeks]
  );
  const { cpm, correlation, corrStrong } = useMemo(() => {
    // Scoped to chartWeeks (the fencepost training window), not the model's full
    // history -- matching portfolioStats below, which the backend now computes the
    // same way (confirmed against real `away` data, 2026-08-24: a channel's own
    // zero-spend-week count was previously counting years of pre-fencepost history the
    // chart never displays).
    const totalSpend = chartWeeks.reduce((sum, w) => sum + w.spend, 0);
    const totalImpressions = chartWeeks.reduce((sum, w) => sum + w.impressions, 0);
    return {
      cpm: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : null,
      correlation: portfolioStats.correlation,
      corrStrong: portfolioStats.correlation > 0.7
    };
  }, [chartWeeks, portfolioStats.correlation]);

  const { unresolvedSpend, unresolvedImpressions, unresolvedRowCount } = portfolioStats;

  const { spendOnlyPlatforms: spendOnly } = portfolioStats;
  const { impressionsOnlyPlatforms: impressionsOnly } = portfolioStats;
  const coverageParts: ReactNode[] = [];
  if (impressionsOnly.length > 0) {
    coverageParts.push(
      <span key='imp'>
        <strong>{impressionsOnly.length} impressions-only</strong> (
        {formatPlatformList(impressionsOnly)})
      </span>
    );
  }
  if (spendOnly.length > 0) {
    coverageParts.push(
      <span key='spend'>
        <strong>{spendOnly.length} spend-only</strong> ({formatPlatformList(spendOnly)})
      </span>
    );
  }

  const kpiTiles: KpiTileData[] = [
    {
      label: 'Spend',
      note: '(weekly avg)',
      value: money0.format(portfolioStats.avgWeeklySpend),
      unit: '/wk'
    },
    {
      label: 'Impressions',
      note: '(weekly avg)',
      value: int0.format(portfolioStats.avgWeeklyImpressions),
      unit: '/wk'
    },
    { label: 'CPM', note: '(blended)', value: cpm === null ? 'N/A' : money2.format(cpm) },
    {
      label: 'Correlation (spend vs. impressions)',
      value: correlation.toFixed(3),
      badge: {
        text: corrStrong ? 'Strong Correlation' : 'Weak Correlation',
        kind: corrStrong ? 'strong' : 'weak'
      }
    }
  ];

  return (
    <div className='data-qa-portfolio'>
      <div className='data-qa-portfolio__card'>
        <div className='data-qa-portfolio__lead'>Total Spend and Impressions</div>
        <div className='data-qa-portfolio__lead-subtitle'>
          Model spend and impressions as compared to the canonical Bliss Point database
        </div>
        <KpiBar tiles={kpiTiles} />
        <DataQaChart
          title='Total Spend Over Time'
          weeks={chartWeeks}
          series={hasBpm ? SPEND_SERIES : SPEND_SERIES.slice(0, 1)}
          divergentRanges={spendRanges}
          bookmarkWeekIso={bookmarkWeekIso}
          isColdStart={isColdStart}
          height={280}
          hideLegend={!hasBpm}
        />
        <DataQaChart
          title='Total Impressions Over Time'
          weeks={chartWeeks}
          series={hasBpm ? IMPRESSIONS_SERIES : IMPRESSIONS_SERIES.slice(0, 1)}
          divergentRanges={impressionsRanges}
          bookmarkWeekIso={bookmarkWeekIso}
          isColdStart={isColdStart}
          height={280}
          hideLegend={!hasBpm}
        />
        {hasBpm && (
          <GroundTruthSqlPanel
            company={company}
            weeks={groundTruthWeeks}
            reviewGranularity={dataset.reviewGranularity}
          />
        )}
      </div>

      {coverageParts.length > 0 && (
        <div className='data-qa-portfolio__note'>
          <strong>Note:</strong> Some paid-media platforms contribute asymmetrically to the
          portfolio totals —{' '}
          {coverageParts.map((part, i) => (
            <span key={i === 0 ? 'first' : 'second'}>
              {i > 0 && ' · '}
              {part}
            </span>
          ))}
          . This can dampen the spend-vs-impressions correlation shown above.
        </div>
      )}

      {hasUnresolvedMedia && (
        <div className='data-qa-portfolio__note'>
          <strong>Note:</strong> {money0.format(unresolvedSpend)} in spend and{' '}
          {int0.format(unresolvedImpressions)} impressions across {unresolvedRowCount} row
          {unresolvedRowCount === 1 ? '' : 's'} had no Redshift channel/platform mapping and are
          excluded from every total above.
        </div>
      )}
    </div>
  );
};
